import React from "react";
import { notification } from "antd";
import {
  ApartmentOutlined,
  CheckCircleFilled,
  CheckCircleOutlined,
  ClusterOutlined,
  CodeOutlined,
  CreditCardOutlined,
  EnvironmentOutlined,
} from "@ant-design/icons";
import { store } from "../../../app/store";

export interface SentWebSocketCommandSummary {
  command?: string;
  position?: number;
  active?: boolean;
  channelId: string;
  bankId?: string;
  liftId?: string;
}

export interface WebSocketCommandNotificationProps {
  command: number;
  position: number;
  active: boolean;
  channel: string;
  bank: string;
  lift: string;
}

function NotificationInfoCard({
  icon,
  label,
  value,
  isLongValue = false,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
  isLongValue?: boolean;
}) {
  const [isHovered, setIsHovered] = React.useState(false);
  const theme = store.getState().home.theme;
  const isDarkTheme = theme === "dark";
  const labelColor = isDarkTheme ? "#ffffff" : "#8c8c8c";
  const valueColor = isDarkTheme ? "#ffffff" : "#262626";
  const cardBorderColor = isDarkTheme ? "#3a3a3a" : "#f0f0f0";
  const cardBackgroundColor = isDarkTheme ? "#1f1f1f" : "#ffffff";
  const cardHoverBackgroundColor = isDarkTheme ? "#262626" : "rgba(82, 196, 26, 0.05)";

  return (
    <div
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      style={{
        border: `1px solid ${cardBorderColor}`,
        background: isHovered ? cardHoverBackgroundColor : cardBackgroundColor,
        padding: 14,
        borderRadius: 8,
        transition: "all 0.2s ease",
        transform: isHovered ? "translateY(-1px)" : "translateY(0)",
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
        <span>{icon}</span>
        <span
          style={{
            color: labelColor,
            fontSize: 12,
            fontWeight: 500,
          }}
        >
          {label}
        </span>
      </div>
      <div
        style={{
          color: valueColor,
          fontSize: isLongValue ? 13 : 14,
          fontWeight: 600,
          lineHeight: 1.4,
          whiteSpace: isLongValue ? "nowrap" : "normal",
        }}
      >
        {value}
      </div>
    </div>
  );
}

export function WebSocketCommandNotification({
  command,
  position,
  active,
  channel,
  bank,
  lift,
}: WebSocketCommandNotificationProps) {
  const successColor = "#52c41a";
  const isSmallScreen = typeof window !== "undefined" && window.innerWidth <= 560;

  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: isSmallScreen ? "minmax(0, 1fr)" : "repeat(2, minmax(0, 1fr))",
        gap: 14,
        width: "100%",
      }}
    >
      <NotificationInfoCard
        icon={<CodeOutlined style={{ fontSize: 20, color: successColor }} />}
        label="Command"
        value={String(command)}
      />
      <NotificationInfoCard
        icon={<EnvironmentOutlined style={{ fontSize: 20, color: successColor }} />}
        label="Position"
        value={String(position)}
      />
      <NotificationInfoCard
        icon={<CheckCircleOutlined style={{ fontSize: 20, color: successColor }} />}
        label="Active"
        value={String(active)}
      />
      <NotificationInfoCard
        icon={<ClusterOutlined style={{ fontSize: 20, color: successColor }} />}
        label="Channel"
        value={channel || "N/A"}
        isLongValue
      />
      <NotificationInfoCard
        icon={<CreditCardOutlined style={{ fontSize: 20, color: successColor }} />}
        label="Bank"
        value={bank || "N/A"}
        isLongValue
      />
      <NotificationInfoCard
        icon={<ApartmentOutlined style={{ fontSize: 20, color: successColor }} />}
        label="Lift"
        value={lift || "N/A"}
        isLongValue
      />
    </div>
  );
}

export const showWebSocketCommandSentNotification = (
  commandSummary: SentWebSocketCommandSummary,
) => {
  const theme = store.getState().home.theme;
  const titleColor = theme === "dark" ? "#ffffff" : "#d9d9d9";
  const showCommandDetails = store.getState().monitoring.showCommandDetails;

  notification.success({
    message: "Command sent succesfully",
    icon: <CheckCircleFilled style={{ color: "#52c41a", fontSize: 20 }} />,
    placement: "top",
    duration: 5,
    style: {
      color: titleColor,
      width: showCommandDetails ? 760 : 420,
      maxWidth: "calc(100vw - 32px)",
      borderRadius: 12,
      borderInlineStart: "4px solid #52c41a",
      boxShadow: "0 8px 24px rgba(15, 23, 42, 0.12)",
    },
    description: showCommandDetails ? (
      <WebSocketCommandNotification
        command={Number(commandSummary.command ?? 0)}
        position={Number(commandSummary.position ?? 0)}
        active={Boolean(commandSummary.active)}
        channel={commandSummary.channelId || "N/A"}
        bank={commandSummary.bankId || "N/A"}
        lift={commandSummary.liftId || "N/A"}
      />
    ) : undefined,
  });
};
